<template>
  <v-dialog v-model="internalShow" persistent :max-width="isDesktop ? '720px' : '95vw'">
    <v-card class="modal-card">
      <v-container>
        <v-layout row justify-space-between>
          <v-card-title class="modal-title">
            {{
              isEdit ? adminText('organizationAll.update_offer_5f2f2713', 'Update offer') : adminText('organizationAll.create_new_offer_50cd7965', 'Create new offer')
            }}
          </v-card-title>
          <v-icon color="#979797" class="close-icon" @click="closeDialog"> close </v-icon>
        </v-layout>
        <v-card-text class="modal-subtitle">
          {{ adminText('organizationAll.define_the_rules_for_granting_free_tickets_pe_c270006b', 'Define the rules for granting free tickets per event.') }}
        </v-card-text>

        <div v-if="isUsed" class="used-notice">
          {{ adminText('organizationAll.this_offer_has_already_been_redeemed_event_ti_d83f2bd9', 'This offer has already been redeemed. Event, ticket, free tickets quantity and total available quantity can no longer be changed.') }}
        </div>

        <div class="form-grid">
          <autocomplete-field
            v-model="form.eventId"
            :items="events"
            item-render="title"
            item-select="id"
            :label="adminText('organizationAll.event_name_16b8ed6e', 'Event name*')"
            box
            :disabled="isUsed"
            :validator="$v.form.eventId"
            style="grid-column: 1 / -1"
          />
          <autocomplete-field
            v-model="form.orderItemId"
            :items="filteredTickets"
            item-render="name"
            item-select="id"
            :label="adminText('organizationAll.ticket_5be34f36', 'Ticket*')"
            box
            :disabled="isUsed"
            :validator="$v.form.orderItemId"
            style="grid-column: 1 / -1"
          />
          <text-field
            v-model.number="form.quantityInterval"
            type="number"
            :label="adminText('organizationAll.quantity_interval_315b9ee9', 'Quantity interval*')"
            :hint="adminText('organizationAll.e_g_every_5_tickets_9da25621', 'e.g., every 5 tickets')"
            box
            :validator="$v.form.quantityInterval"
          />
          <text-field
            v-model.number="form.freeQuantity"
            type="number"
            :label="adminText('organizationAll.free_tickets_quantity_48ef1506', 'Free tickets quantity*')"
            :hint="adminText('organizationAll.e_g_1_free_ticket_4e9384a0', 'e.g., 1 free ticket')"
            box
            :disabled="isUsed"
            :validator="$v.form.freeQuantity"
          />
          <text-field
            v-model.number="form.totalQuantity"
            type="number"
            :label="adminText('organizationAll.total_available_quantity_7d5754c5', 'Total available quantity*')"
            box
            :validator="$v.form.totalQuantity"
            :disabled="isUsed || (form && form.unlimitedQuantity)"
          />
          <v-checkbox
            v-model="form.unlimitedQuantity"
            :disabled="isUsed"
            :label="adminText('organizationAll.unlimited_total_quantity_fd890688', 'Unlimited total quantity')"
          />
          <text-field
            v-model.number="form.quantityPerOrder"
            type="number"
            :label="adminText('organizationAll.quantity_per_order_f61980b9', 'Quantity per order')"
            box
            :validator="$v.form.quantityPerOrder"
            style="grid-column: 1 / -1"
          />
          <datetime-field v-model="form.from" :validator="$v.form.from" :label="adminText('organizationAll.start_date_2fde03a5', 'Start date*')" />
          <datetime-field
            v-model="form.to"
            :validator="$v.form.to"
            :label="adminText('organizationAll.end_date_dc7fcbdc', 'End date*')"
            :min="form.from ? form.from.toString() : null"
          />
        </div>
      </v-container>

      <div class="buttons">
        <main-button flat outline :disabled="loading" @click="closeDialog">{{ adminText('organizationAll.cancel_77dfd213', 'Cancel') }}</main-button>
        <main-button :loading="loading" depressed @click="submitForm">
          {{ isEdit ? adminText('organizationAll.update_fb91e24f', 'Update') : adminText('organizationAll.create_6e157c5d', 'Create') }}
        </main-button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { required, minValue, numeric, requiredIf, maxValue } from 'vuelidate/lib/validators'
import moment from 'moment'
import { GET_ALL_ACTIVE_ORGANIZATION_TICKETS } from '@/graphql/orderItem.graphql'
import { CREATE_BUNDLE_OFFER, UPDATE_BUNDLE_OFFER } from '@/graphql/bundleOffer.graphql'
import DatetimePicker from '@/components/shared/inputs/datetime-field.vue'
import { defaultMessages, getValidationErrors } from '@/utils'
import { uniqBy, flatten } from 'lodash'
import Checkbox from '@/components/shared/inputs/checkbox.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'bundle-offer-modal',
  components: { DatetimePicker, Checkbox },
  props: {
    show: Boolean,
    organizationSlug: String,
    bundleOffer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      isDesktop: typeof window !== 'undefined' ? window.innerWidth > 800 : true,
      events: [],
      allTickets: [],
      form: this.getFormInitialValues(),
    }
  },
  validations() {
    const afterFrom = value => {
      if (!value || !this.form.from) return true

      return moment(value).isAfter(moment(this.form.from))
    }

    return {
      form: {
        eventId: { required },
        orderItemId: { required },
        quantityInterval: { required, numeric, minValue: minValue(1), maxValue: maxValue(10) },
        freeQuantity: { required, numeric, minValue: minValue(1), maxValue: maxValue(10) },
        totalQuantity: {
          numeric,
          minValue: minValue(1),
          required: requiredIf(() => !this.form.unlimitedQuantity),
        },
        from: { required },
        to: { required, isAfterFrom: afterFrom },
      },
    }
  },
  apollo: {
    getAllActiveOrganizationTickets: {
      query: GET_ALL_ACTIVE_ORGANIZATION_TICKETS,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          includeOrderItemId: this.form.orderItemId,
        }
      },
      result({ data, errors }) {
        if (!errors && data && data.getAllActiveOrganizationTickets) {
          // Filter out tickets that already have a bundle offer and are not free
          this.allTickets = data.getAllActiveOrganizationTickets
            .filter(
              ticket =>
                !ticket.bundleOfferId ||
                (this.bundleOffer && ticket.bundleOfferId === this.bundleOffer.id),
            )
            .filter(ticket => Number(ticket.price) > 0)

          this.events = uniqBy(flatten(this.allTickets.map(e => e.event)), 'id')
          this.events = this.events.map(event => ({
            ...event,
            title: event.eventTemplateId
              ? `${event.title} (${moment(event.from).format('DD.MM.YYYY.')})`
              : event.title,
          }))
        }
      },
    },
  },
  computed: {
    errorMessages() {
      return {
        ...this.adminValidationMessages(defaultMessages),
        isAfterFrom: 'End date must be after start date',
      }
    },
    isEdit() {
      return !!this.form.id
    },
    isUsed() {
      return !!this.bundleOffer && Number(this.bundleOffer.quantityUsed) > 0
    },
    filteredTickets() {
      if (!this.form.eventId) return []

      return this.allTickets.filter(ticket => ticket.event.id === this.form.eventId)
    },
    internalShow: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      },
    },
  },
  watch: {
    'form.unlimitedQuantity': {
      handler(val) {
        if (val) {
          this.form.totalQuantity = null
        }
      },
    },
    bundleOffer: {
      handler() {
        this.form = this.getFormInitialValues()
      },
      deep: true,
    },
  },
  created() {
    window.addEventListener('resize', this.resizeHandler)
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    resizeHandler() {
      this.isDesktop = window.innerWidth > 800
    },
    getErrors(field) {
      return getValidationErrors(this.errorMessages)(field)
    },
    closeDialog() {
      this.$v.$reset()
      this.internalShow = false
      this.form = this.getFormInitialValues()
    },
    async submitForm() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.loading = true
      try {
        if (this.isEdit) {
          const { errors } = await this.$apollo.mutate({
            mutation: UPDATE_BUNDLE_OFFER,
            variables: {
              id: this.form.id,
              eventId: this.form.eventId,
              orderItemId: this.form.orderItemId,
              quantityInterval: Number(this.form.quantityInterval),
              freeQuantity: Number(this.form.freeQuantity),
              unlimitedQuantity: this.form.unlimitedQuantity,
              totalQuantity: this.form.unlimitedQuantity ? null : Number(this.form.totalQuantity),
              quantityPerOrder: this.form.quantityPerOrder
                ? Number(this.form.quantityPerOrder)
                : null,
              from: this.form.from,
              to: this.form.to,
              active: this.form.active,
            },
          })
          if (errors && errors.length > 0) {
            this.$emit('error', errors[0].message)

            return
          }
        } else {
          const { errors } = await this.$apollo.mutate({
            mutation: CREATE_BUNDLE_OFFER,
            variables: {
              eventId: this.form.eventId,
              orderItemId: this.form.orderItemId,
              quantityInterval: Number(this.form.quantityInterval),
              freeQuantity: Number(this.form.freeQuantity),
              unlimitedQuantity: !!this.form.unlimitedQuantity,
              totalQuantity: this.form.unlimitedQuantity ? null : Number(this.form.totalQuantity),
              quantityPerOrder: this.form.quantityPerOrder
                ? Number(this.form.quantityPerOrder)
                : null,
              from: this.form.from,
              to: this.form.to,
              active: this.form.active,
            },
          })
          if (errors && errors.length > 0) {
            this.$emit('error', errors[0].message)

            return
          }
        }
        this.$emit('saved')
        this.internalShow = false
      } catch (e) {
        // Intentionally not swallowing the error; snackbar handled by parent if desired
        // eslint-disable-next-line no-console
        console.error(e)
        this.$emit('error', 'An error occurred')
      } finally {
        this.loading = false
      }
    },
    getFormInitialValues() {
      return {
        id: this.bundleOffer && this.bundleOffer.id ? this.bundleOffer.id : null,
        eventId: this.bundleOffer && this.bundleOffer.eventId ? this.bundleOffer.eventId : null,
        orderItemId:
          this.bundleOffer && this.bundleOffer.orderItem ? this.bundleOffer.orderItem.id : null,
        quantityInterval:
          this.bundleOffer && this.bundleOffer.quantityInterval
            ? this.bundleOffer.quantityInterval
            : null,
        freeQuantity:
          this.bundleOffer && this.bundleOffer.freeQuantity ? this.bundleOffer.freeQuantity : null,
        totalQuantity:
          this.bundleOffer && typeof this.bundleOffer.totalQuantity !== 'undefined'
            ? this.bundleOffer.totalQuantity
            : null,
        unlimitedQuantity: this.bundleOffer ? this.bundleOffer.totalQuantity == null : false,
        quantityPerOrder:
          this.bundleOffer && this.bundleOffer.quantityPerOrder
            ? this.bundleOffer.quantityPerOrder
            : null,
        from: this.bundleOffer && this.bundleOffer.from ? this.bundleOffer.from : null,
        to: this.bundleOffer && this.bundleOffer.to ? this.bundleOffer.to : null,
        active:
          this.bundleOffer && typeof this.bundleOffer.active !== 'undefined'
            ? this.bundleOffer.active
            : true,
      }
    },
  },
}
</script>

<style scoped>
.modal-card {
  box-shadow: 0 0 24px rgba(51, 51, 51, 0.2);
  border-radius: 12px;
}
.modal-title {
  color: #0277bd;
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.6px;
  padding: 0;
  margin-bottom: 12px;
}
.modal-subtitle {
  color: #979797;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  line-height: 150%;
  padding: 0;
  margin-bottom: 24px;
}
.used-notice {
  font-family: 'Source Sans Pro';
  font-size: 14px;
  line-height: 150%;
  color: #0277bd;
  background: #e3f2fd;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
}
@media screen and (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
.buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding: 1rem;
}
</style>
